/**
 * Modal de recadrage photo (ratio 1:1 par défaut — photo d'identité).
 * - L'utilisateur peut zoomer, déplacer, et faire pivoter l'image
 * - Validation : extrait la zone sélectionnée en File JPEG (qualité 0.9)
 * - Utilise react-easy-crop pour l'interaction et un canvas pour l'extraction
 */
import React, { useState, useCallback } from 'react';
import CropperRaw from 'react-easy-crop';
import { X, Check, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';

// Cast en ComponentType<any> : les types de react-easy-crop@6 ne sont pas
// compatibles avec React 19 (problème connu, le composant fonctionne au runtime).
const Cropper = CropperRaw as unknown as React.ComponentType<any>;

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Props {
  open: boolean;
  imageSrc: string;
  onClose: () => void;
  onValidate: (croppedFile: File) => void;
  filename?: string;
  aspect?: number; // ratio largeur/hauteur, 1 par défaut (carré photo identité)
  outputSize?: number; // taille du côté du JPEG produit (px), 600 par défaut
}

const PhotoCropModal: React.FC<Props> = ({
  open, imageSrc, onClose, onValidate,
  filename = 'photo.jpg', aspect = 1, outputSize = 600,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_: Area, areaPx: Area) => {
    setCroppedAreaPixels(areaPx);
  }, []);

  const handleValidate = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const file = await getCroppedFile(imageSrc, croppedAreaPixels, rotation, outputSize, filename);
      onValidate(file);
    } catch (e) {
      console.error('Erreur recadrage:', e);
      alert("Erreur lors du recadrage de l'image");
    } finally {
      setProcessing(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '95vh' }}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Recadrer la photo
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        {/* Zone de cropping */}
        <div className="relative bg-gray-900" style={{ width: '100%', height: '420px' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            objectFit="contain"
            showGrid
          />
        </div>

        {/* Contrôles */}
        <div className="p-4 space-y-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <ZoomOut size={16} className="text-gray-500" />
            <input
              type="range"
              min={1} max={4} step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1"
              aria-label="Zoom"
            />
            <ZoomIn size={16} className="text-gray-500" />
            <span className="text-xs text-gray-600 dark:text-gray-400 w-12 text-right">{zoom.toFixed(1)}×</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => setRotation((r) => (r + 90) % 360)}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-sm"
              type="button"
            >
              <RotateCw size={14} /> Pivoter 90°
            </button>
            <span className="text-xs text-gray-500">
              Glissez l'image pour la déplacer · Molette / pincement pour zoomer
            </span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={processing}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            Annuler
          </button>
          <button
            onClick={handleValidate}
            disabled={processing || !croppedAreaPixels}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-medium rounded"
          >
            <Check size={16} /> {processing ? 'Traitement…' : 'Valider'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────── Helpers ─────────────────────────────────────────────────────────────

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (err) => reject(err));
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
  });
}

function getRadianAngle(degree: number): number {
  return (degree * Math.PI) / 180;
}

/**
 * Extrait la zone recadrée + rotation et retourne un File JPEG redimensionné.
 */
async function getCroppedFile(
  imageSrc: string,
  pixelCrop: Area,
  rotation: number,
  outputSize: number,
  filename: string,
): Promise<File> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context indisponible');

  const safeArea = Math.max(image.width, image.height) * 2;
  canvas.width = safeArea;
  canvas.height = safeArea;
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-safeArea / 2, -safeArea / 2);
  ctx.drawImage(image, safeArea / 2 - image.width / 2, safeArea / 2 - image.height / 2);

  const data = ctx.getImageData(0, 0, safeArea, safeArea);
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.putImageData(
    data,
    -safeArea / 2 + image.width / 2 - pixelCrop.x,
    -safeArea / 2 + image.height / 2 - pixelCrop.y,
  );

  // Redimensionner à outputSize × outputSize (proportionnel selon aspect)
  const finalCanvas = document.createElement('canvas');
  const finalCtx = finalCanvas.getContext('2d');
  if (!finalCtx) throw new Error('Canvas 2D context indisponible');
  finalCanvas.width = outputSize;
  finalCanvas.height = outputSize;
  finalCtx.fillStyle = '#fff';
  finalCtx.fillRect(0, 0, outputSize, outputSize);
  finalCtx.drawImage(canvas, 0, 0, outputSize, outputSize);

  return new Promise<File>((resolve, reject) => {
    finalCanvas.toBlob(
      (blob) => {
        if (!blob) { reject(new Error('Blob vide')); return; }
        const safeName = filename.toLowerCase().endsWith('.jpg') || filename.toLowerCase().endsWith('.jpeg')
          ? filename
          : filename.replace(/\.[^.]+$/, '') + '.jpg';
        resolve(new File([blob], safeName, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.9,
    );
  });
}

export default PhotoCropModal;
